import { collection, getDocs } from "firebase/firestore";
import { format } from "date-fns";
import {
  AlertCircle,
  ChevronRight,
  CloudOff,
  Download,
  Loader2,
  Search,
  Trash2,
  UsersRound,
} from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { db } from "integrations/firebase/firestore";

import { AppShell } from "components/AppShell";
import { AvatarPlaceholder } from "components/AvatarPlaceholder";
import { CardGrid } from "components/CardGrid";
import { EmptyState, LoadingCards } from "components/states";
import { ToggleChip } from "components/ToggleChip";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card } from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";

import { EventPersonSheet } from "../event-experience/EventPersonSheet";
import { personNoteSchema } from "../person-notes/schemas";
import { ctaButtonClass } from "./button-styles";
import { formatCode } from "./connect-code";
import {
  connectionsCsv,
  connectionsVCard,
  exportFileName,
  shareOrDownload,
} from "./export";
import { eventsNewestFirst, type Connection } from "./schemas";
import {
  fileConnectionRequest,
  watchConnectionRequest,
} from "./use-connection-request";
import {
  useConnections,
  usePendingConnects,
  type PendingConnect,
} from "./use-connections";

/**
 * How long a removal may wait for the trigger before its card comes back. A
 * REMOVE_CONNECTION the server never settles (the trigger failed to run, or
 * the watch lost its connection) would otherwise hide the card for the rest
 * of the session, with nothing on screen to say it is still in the list.
 */
export const REMOVAL_TIMEOUT_MS = 30_000;

/**
 * Everybody the person connected with by QR (TNG-93), across events. A card
 * opens the profile sheet with the person's private note; Remove takes them
 * out of this list only. Export hands the list over as CSV or vCard.
 */
export function ConnectionsPage({
  uid,
  email,
  signOut,
  initialEventFilter,
}: {
  uid: string;
  email: string | null;
  signOut: () => void;
  /** An event id to filter by from the start. */
  initialEventFilter?: string;
}) {
  const state = useConnections(uid);
  const { pending, failed, dismiss } = usePendingConnects(uid);
  const [search, setSearch] = useState("");
  const [eventFilter, setEventFilter] = useState<string | null>(
    initialEventFilter ?? null,
  );
  const [selected, setSelected] = useState<Connection | null>(null);
  // Cards hidden while their REMOVE_CONNECTION is on its way: "pending" until
  // the trigger answers, "done" once it succeeded and until the list itself
  // no longer has the card. Then the entry goes, so connecting with the same
  // person again in this session shows their card.
  const [removing, setRemoving] = useState<
    ReadonlyMap<string, "pending" | "done">
  >(new Map());
  const watches = useRef(new Set<() => void>());
  const mounted = useRef(true);
  const [confirmRemove, setConfirmRemove] = useState<Connection | null>(null);
  const [exporting, setExporting] = useState(false);
  const searchId = useId();

  useEffect(() => {
    const stops = watches.current;
    mounted.current = true;
    return () => {
      mounted.current = false;
      stops.forEach((stop) => stop());
      stops.clear();
    };
  }, []);

  const listed = state.status === "success" ? state.connections : null;
  useEffect(() => {
    if (!listed) return;
    const present = new Set(listed.map((c) => c.otherUid));
    const gone = [...removing].filter(
      ([otherUid, stage]) => stage === "done" && !present.has(otherUid),
    );
    if (gone.length === 0) return;
    setRemoving((current) => {
      const next = new Map(current);
      for (const [otherUid] of gone) next.delete(otherUid);
      return next;
    });
  }, [listed, removing]);

  const connections = useMemo(
    () =>
      state.status === "success"
        ? state.connections.filter((c) => !removing.has(c.otherUid))
        : [],
    [state, removing],
  );

  // Every event somebody was met at, most recent first, for the filter.
  const events = useMemo(() => {
    const seen = new Map<string, { title: string; at: number }>();
    for (const connection of connections) {
      for (const tag of eventsNewestFirst(connection)) {
        const at = tag.connectedAt.toMillis();
        const known = seen.get(tag.eventId);
        if (!known || known.at < at) {
          seen.set(tag.eventId, { title: tag.title, at });
        }
      }
    }
    return [...seen.entries()]
      .sort((a, b) => b[1].at - a[1].at)
      .map(([eventId, { title }]) => ({ eventId, title }));
  }, [connections]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return connections.filter(
      (connection) =>
        (!eventFilter || eventFilter in connection.events) &&
        (!needle ||
          [
            connection.name,
            connection.role,
            connection.company,
            connection.email,
          ]
            .filter(Boolean)
            .some((value) => value!.toLowerCase().includes(needle))),
    );
  }, [connections, search, eventFilter]);

  const remove = (connection: Connection) => {
    setConfirmRemove(null);
    setSelected(null);
    const { otherUid, name } = connection;
    const mark = (stage: "pending" | "done" | null) =>
      setRemoving((current) => {
        const next = new Map(current);
        if (stage) next.set(otherUid, stage);
        else next.delete(otherUid);
        return next;
      });
    const restore = (reason?: string) => {
      mark(null);
      toast.error(
        `Could not remove ${name}. They are back in your list; please try again.${
          reason ? ` (${reason})` : ""
        }`,
      );
    };
    // Whichever comes first settles the removal: the trigger's answer, a
    // refused write, or the timeout. Anything after that is ignored.
    let settled = false;
    let stopWatch: (() => void) | null = null;
    const cancelTimeout = () => window.clearTimeout(timer);
    const settle = (): boolean => {
      if (settled) return false;
      settled = true;
      cancelTimeout();
      watches.current.delete(cancelTimeout);
      if (stopWatch) {
        stopWatch();
        watches.current.delete(stopWatch);
      }
      return true;
    };
    const timer = window.setTimeout(() => {
      if (!settle() || !mounted.current) return;
      restore("the app did not hear back in time");
    }, REMOVAL_TIMEOUT_MS);
    watches.current.add(cancelTimeout);
    mark("pending");
    const { ref, written } = fileConnectionRequest(uid, "REMOVE_CONNECTION", {
      otherUid,
    });
    written.then(
      () => {
        if (!mounted.current || settled) return;
        const stop = watchConnectionRequest(ref, (outcome) => {
          if (!settle()) return;
          if (outcome.status === "succeeded") {
            mark("done");
            toast.success(`Removed ${name} from your connections.`, {
              duration: 3000,
            });
          } else {
            restore(outcome.error);
          }
        });
        if (settled) {
          stop();
          return;
        }
        stopWatch = stop;
        watches.current.add(stop);
      },
      () => {
        if (settle()) restore();
      },
    );
  };

  return (
    <AppShell
      identity={{ email }}
      onSignOut={signOut}
      title="Connections"
      actions={
        connections.length > 0 ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setExporting(true)}
            data-testid="connections-export"
          >
            <Download />
            Export
          </Button>
        ) : undefined
      }
    >
      <p className="mb-6 max-w-2xl text-sm text-muted-foreground">
        People you swapped codes with at events. Each card is what they shared
        when you met; meeting again updates it. Removing someone takes them off
        this list only.
      </p>

      {failed.length > 0 && (
        <div className="mb-6 space-y-2" data-testid="connections-failed">
          {failed.map((item) => (
            <Card
              key={item.id}
              role="alert"
              className="flex flex-wrap items-center gap-3 border-destructive/50 p-4"
              data-testid="connections-failed-row"
            >
              <AlertCircle
                className="h-5 w-5 shrink-0 text-destructive-text dark:text-hb-red-text"
                aria-hidden
              />
              <p className="min-w-0 flex-1 text-sm text-foreground">
                <span className="font-mono font-semibold">
                  {formatCode(item.code)}
                </span>
                {" · "}
                {item.error}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => dismiss(item.id)}
              >
                Dismiss
              </Button>
            </Card>
          ))}
        </div>
      )}

      {pending.length > 0 && (
        <div className="mb-6 space-y-2" data-testid="connections-pending">
          {pending.map((item) => (
            <PendingRow key={item.id} item={item} />
          ))}
        </div>
      )}

      {state.status === "loading" && <LoadingCards cards={3} mode="grid" />}

      {state.status === "error" && (
        <EmptyState
          icon={AlertCircle}
          title="Could not load your connections"
          testId="connections-error"
        >
          {state.error}
        </EmptyState>
      )}

      {state.status === "success" && connections.length === 0 && (
        <EmptyState
          icon={UsersRound}
          title="No connections yet"
          testId="connections-empty"
        >
          At an event with connections switched on, open the Connect card on the
          Attendees tab and scan somebody&apos;s code, or show them yours.
        </EmptyState>
      )}

      {connections.length > 0 && (
        <div className="space-y-4" data-testid="connections">
          <div className="relative max-w-md">
            <label htmlFor={searchId} className="sr-only">
              Search connections
            </label>
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              id={searchId}
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name, role or company"
              className="h-11 pl-9 text-base sm:text-sm"
              data-testid="connections-search"
            />
          </div>

          {/* Also with one event while a filter is on, so a filter that came
              with the link can always be cleared. */}
          {(events.length > 1 || eventFilter !== null) && (
            <div
              className="flex flex-wrap gap-2"
              role="group"
              aria-label="Filter by event"
            >
              <ToggleChip
                selected={eventFilter === null}
                onClick={() => setEventFilter(null)}
                className="min-h-11 px-4 text-sm"
              >
                All events
              </ToggleChip>
              {events.map((event) => (
                <ToggleChip
                  key={event.eventId}
                  selected={eventFilter === event.eventId}
                  onClick={() =>
                    setEventFilter((current) =>
                      current === event.eventId ? null : event.eventId,
                    )
                  }
                  className="min-h-11 max-w-full px-4 text-sm wrap-anywhere"
                  data-testid={`connections-filter-${event.eventId}`}
                >
                  {event.title || "Event"}
                </ToggleChip>
              ))}
            </div>
          )}

          <p
            className="text-sm text-muted-foreground empty:hidden"
            role="status"
          >
            {visible.length !== connections.length
              ? `${visible.length} of ${connections.length} shown`
              : ""}
          </p>

          {visible.length === 0 ? (
            <EmptyState icon={Search} title="No connections match">
              Try a different search or another event.
            </EmptyState>
          ) : (
            <CardGrid className="gap-3 lg:grid-cols-2">
              {visible.map((connection) => (
                <ConnectionCard
                  key={connection.otherUid}
                  connection={connection}
                  onSelect={() => setSelected(connection)}
                />
              ))}
            </CardGrid>
          )}
        </div>
      )}

      <ConnectionSheet
        connection={selected}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
        onRemove={(connection) => setConfirmRemove(connection)}
      />

      <Dialog
        open={confirmRemove !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmRemove(null);
        }}
      >
        <DialogContent data-testid="connection-remove-dialog">
          <DialogHeader>
            <DialogTitle>Remove {confirmRemove?.name}?</DialogTitle>
            <DialogDescription>
              They leave your connections. You stay in theirs, your private
              notes about them are kept, and they can still use your code. To
              stop that, open &ldquo;Show my code&rdquo; at an event and choose
              &ldquo;New code&rdquo;.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmRemove(null)}
            >
              Keep
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="dark:border-hb-red-text dark:text-foreground"
              onClick={() => confirmRemove && remove(confirmRemove)}
              data-testid="connection-remove-confirm"
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ExportDialog
        uid={uid}
        open={exporting}
        onOpenChange={setExporting}
        connections={visible}
      />
    </AppShell>
  );
}

function PendingRow({ item }: { item: PendingConnect }) {
  return (
    <Card
      className="flex items-center gap-3 border-border p-4"
      data-testid="connections-pending-row"
    >
      {item.queued && !navigator.onLine ? (
        <CloudOff
          className="h-5 w-5 shrink-0 text-muted-foreground"
          aria-hidden
        />
      ) : (
        <Loader2
          className="h-5 w-5 shrink-0 animate-spin text-muted-foreground"
          aria-hidden
        />
      )}
      <p className="min-w-0 text-sm text-foreground">
        <span className="font-mono font-semibold">{formatCode(item.code)}</span>
        {" · "}
        {item.queued && !navigator.onLine
          ? "Will connect when you're back online."
          : "Connecting…"}
      </p>
    </Card>
  );
}

function ConnectionCard({
  connection,
  onSelect,
}: {
  connection: Connection;
  onSelect: () => void;
}) {
  const tags = eventsNewestFirst(connection);
  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-full min-w-0 rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      data-testid={`connection-card-${connection.otherUid}`}
    >
      <Card className="flex h-full items-center gap-3 border-border p-4 transition-colors hover:border-lit">
        {connection.avatar ? (
          <img
            src={connection.avatar}
            alt=""
            className="h-12 w-12 shrink-0 rounded-full object-cover"
          />
        ) : (
          <AvatarPlaceholder name={connection.name} size="sm" />
        )}
        <div className="min-w-0 flex-1">
          <p
            className="truncate font-medium text-foreground"
            data-testid="connection-name"
          >
            {connection.name}
          </p>
          {(connection.role || connection.company) && (
            <p className="truncate text-sm text-muted-foreground">
              {[connection.role, connection.company]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
          <div className="mt-1 flex min-w-0 flex-wrap gap-1">
            {tags.map((tag) => (
              <Badge
                key={tag.eventId}
                variant="outline"
                className="max-w-full min-w-0 shrink whitespace-normal wrap-anywhere rounded-lg text-left"
              >
                {tag.title || "Event"}
              </Badge>
            ))}
          </div>
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      </Card>
    </button>
  );
}

/**
 * The shared person sheet, opened with the ids of the most recent meeting:
 * the event and the person's roster row there, plus whose connection this
 * is. It shows the card they shared when you met, and the private note. What
 * is left here is what only this page does: where you met, and Remove.
 */
function ConnectionSheet({
  connection,
  onOpenChange,
  onRemove,
}: {
  connection: Connection | null;
  onOpenChange: (open: boolean) => void;
  onRemove: (connection: Connection) => void;
}) {
  const tags = connection ? eventsNewestFirst(connection) : [];
  const latest = tags[0];
  return (
    <EventPersonSheet
      eventId={latest?.eventId ?? ""}
      person={connection && latest ? { attendeeId: latest.attendeeId } : null}
      connectionUid={connection?.otherUid}
      onClose={() => onOpenChange(false)}
    >
      {connection && (
        <div className="space-y-6">
          <ul
            className="space-y-1 text-sm text-muted-foreground"
            data-testid="connection-met-at"
          >
            {tags.map((tag) => (
              <li key={tag.eventId}>
                Met at{" "}
                <span className="font-medium text-foreground">
                  {tag.title || "an event"}
                </span>{" "}
                on {format(tag.connectedAt.toDate(), "d MMM yyyy")}
              </li>
            ))}
          </ul>
          <Button
            type="button"
            variant="destructive"
            className="w-full dark:border-hb-red-text dark:text-foreground"
            onClick={() => onRemove(connection)}
            data-testid="connection-remove"
          >
            <Trash2 />
            Remove from my connections
          </Button>
        </div>
      )}
    </EventPersonSheet>
  );
}

async function readNotes(uid: string): Promise<Map<string, string>> {
  const snapshot = await getDocs(collection(db, "users", uid, "person_notes"));
  const notes = new Map<string, string>();
  snapshot.forEach((item) => {
    const parsed = personNoteSchema.safeParse({ id: item.id, ...item.data() });
    if (parsed.success) notes.set(parsed.data.id, parsed.data.body);
  });
  return notes;
}

function ExportDialog({
  uid,
  open,
  onOpenChange,
  connections,
}: {
  uid: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connections: Connection[];
}) {
  const [includeNotes, setIncludeNotes] = useState(false);
  const [busy, setBusy] = useState(false);
  const notesId = useId();

  const run = async (kind: "csv" | "vcf") => {
    setBusy(true);
    try {
      const notes = includeNotes ? await readNotes(uid) : undefined;
      const options = { includeNotes, notes };
      const file =
        kind === "csv"
          ? new File(
              [connectionsCsv(connections, options)],
              exportFileName("csv", new Date()),
              { type: "text/csv;charset=utf-8" },
            )
          : new File(
              [connectionsVCard(connections, options)],
              exportFileName("vcf", new Date()),
              { type: "text/vcard;charset=utf-8" },
            );
      const outcome = await shareOrDownload(file);
      if (outcome !== "cancelled") onOpenChange(false);
    } catch {
      toast.error("Could not export your connections. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="connections-export-dialog">
        <DialogHeader>
          <DialogTitle>Export connections</DialogTitle>
          <DialogDescription>
            {connections.length === 1
              ? "One connection"
              : `${connections.length} connections`}{" "}
            as a spreadsheet (CSV) or for your address book (vCard). A file you
            share cannot be called back.
          </DialogDescription>
        </DialogHeader>
        <label
          htmlFor={notesId}
          className="flex min-h-11 items-center gap-3 text-sm text-foreground"
        >
          <input
            id={notesId}
            type="checkbox"
            checked={includeNotes}
            onChange={(event) => setIncludeNotes(event.target.checked)}
            className="h-5 w-5 shrink-0 accent-lit"
            data-testid="connections-export-notes"
          />
          Include my private notes
        </label>
        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => void run("vcf")}
            data-testid="connections-export-vcard"
          >
            vCard
          </Button>
          <Button
            type="button"
            className={ctaButtonClass}
            disabled={busy}
            onClick={() => void run("csv")}
            data-testid="connections-export-csv"
          >
            CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
